import React, { useMemo } from 'react';
import { ReservationEventReturn } from '../clients/rust-api-client';
import { useAuthorNames } from '../violation/comments/useAuthorNames';
import { formatLocalRange, formatMoment, ruleLabel, statusLabel } from './reservationMeta';
import { ReservationStatusIcon } from './ReservationStatusIcon';

/**
 * A reservation's history, from its append-only event trail: who asked,
 * who decided and why, what happened to the fee, and every rule a
 * manager overrode with the reason they gave. The trail is the record a
 * board reads back when someone asks why one household got the clubhouse
 * three weekends running, so nothing here is summarised away.
 */
const EVENT_TEXT: Record<string, string> = {
  requested: 'requested the booking',
  auto_approved: 'booked it — confirmed automatically',
  approved: 'approved it',
  denied: 'denied the request',
  cancelled: 'cancelled it',
  expired: 'request expired without a decision',
  completed: 'marked it completed',
  fee_posted: 'charged the fee to the home’s account',
  fee_reversed: 'reversed the fee',
  fee_kept: 'kept the fee — cancelled after the deadline',
  refund_due: 'could not reverse the fee — it was already paid; refund due',
  fee_returned: 'returned the paid fee to the home’s account',
  override: 'overrode the booking rules',
  reminder_sent: 'sent the household a reminder',
  rescheduled: 'moved it',
  no_show: 'marked it a no-show',
  late_fee_posted: 'charged a late-cancellation fee',
  notes_updated: 'updated the notes',
  deposit_held: 'charged the deposit to the home’s account',
  deposit_released: 'returned the deposit',
  deposit_refund_due: 'could not reverse the deposit — it was already paid; refund due',
  deposit_returned: 'returned the paid deposit to the home’s account',
  deposit_settled: 'settled the deposit',
  document_submitted: 'uploaded a required document',
  document_accepted: 'accepted a document',
  document_rejected: 'rejected a document',
  inspection_recorded: 'recorded an inspection',
  checked_in: 'checked the household in',
  checked_out: 'checked the household out',
};

const eventText = (type: string): string => EVENT_TEXT[type] ?? type.replace(/_/g, ' ');

/** The rule codes an override event carries in its `detail` object. */
export const overriddenCodes = (detail: object | null | undefined): string[] => {
  const codes = (detail as { codes?: unknown } | null | undefined)?.codes;
  return Array.isArray(codes) ? codes.filter((c): c is string => typeof c === 'string') : [];
};

/** The old and new times a `rescheduled` event carries in its `detail` object. */
export const movedFrom = (
  detail: object | null | undefined
): { from_start: string; from_end: string; to_start: string; to_end: string } | null => {
  const d = detail as Record<string, unknown> | null | undefined;
  const keys = ['from_start', 'from_end', 'to_start', 'to_end'] as const;
  if (!d || !keys.every((k) => typeof d[k] === 'string')) return null;
  return {
    from_start: d.from_start as string,
    from_end: d.from_end as string,
    to_start: d.to_start as string,
    to_end: d.to_end as string,
  };
};

export const ReservationActivity: React.FC<{
  events: ReservationEventReturn[];
  /** The booker's name from the reservation, used for its `requested` event. */
  bookedByName?: string | null;
  /** The section heading. Defaults to "Activity". */
  title?: string;
}> = ({ events, bookedByName, title = 'Activity' }) => {
  const actors = useMemo(
    () =>
      events
        .filter((e) => e.actor_id && (e.actor_kind === 'user' || e.actor_kind === 'member'))
        .map((e) => ({
          author_id: e.actor_id!,
          author_realm: e.actor_kind === 'member' ? ('member' as const) : ('user' as const),
        })),
    [events]
  );
  const actorName = useAuthorNames(actors);

  const who = (e: ReservationEventReturn): string => {
    if (e.actor_kind === 'system' || !e.actor_id) return 'System';
    if (e.event_type === 'requested' && bookedByName) return bookedByName;
    return actorName({
      author_id: e.actor_id,
      author_realm: e.actor_kind === 'member' ? 'member' : 'user',
    });
  };

  const ordered = useMemo(
    () => [...events].sort((a, b) => (a.date_created ?? 0) - (b.date_created ?? 0)),
    [events]
  );

  return (
    <section aria-labelledby="reservation-activity">
      <h2 id="reservation-activity" className="mb-3 text-[15px] font-semibold text-fg">
        {title}
      </h2>
      {ordered.length === 0 ? (
        <p className="text-[13px] text-fg-muted">Nothing recorded yet.</p>
      ) : (
        <ol className="relative space-y-4 before:absolute before:bottom-3 before:left-[11px] before:top-3 before:w-px before:bg-line">
          {ordered.map((e) => {
            const codes = e.event_type === 'override' ? overriddenCodes(e.detail) : [];
            const moved = e.event_type === 'rescheduled' ? movedFrom(e.detail) : null;
            return (
              <li key={e.id} className="relative flex gap-3 text-[13px]">
                <span className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-panel ring-1 ring-line">
                  <ReservationStatusIcon status={e.to_status ?? 'none'} />
                </span>
                <div className="min-w-0 pt-0.5">
                  <p className="text-fg-secondary">
                    <span className="font-medium text-fg">{who(e)}</span> {eventText(e.event_type)}
                    {e.to_status && e.from_status && e.to_status !== e.from_status && (
                      <span className="text-fg-muted">
                        {' '}
                        ({statusLabel(e.from_status)} → {statusLabel(e.to_status)})
                      </span>
                    )}
                    <span className="text-fg-muted"> · {formatMoment(e.date_created)}</span>
                  </p>
                  {moved && (
                    <p className="mt-0.5 text-[12px] text-fg-muted">
                      From {formatLocalRange(moved.from_start, moved.from_end)} to{' '}
                      {formatLocalRange(moved.to_start, moved.to_end)}
                    </p>
                  )}
                  {codes.length > 0 && (
                    <p className="mt-0.5 text-[12px] text-fg-muted">
                      Rules overridden: {codes.map(ruleLabel).join(', ')}
                    </p>
                  )}
                  {e.reason && (
                    <p className="mt-1 whitespace-pre-line rounded-md border border-line px-3 py-2 text-fg-secondary">
                      {e.reason}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};

export default ReservationActivity;
